import { FC, useEffect, useState } from 'react';
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  getDevices,
  getQueueSchedule,
  getQuickStatistics,
  updateQueueSchedule,
} from '../../services';
import { IDeviceListFilter, IStatistics } from '../../types';
import { useGlobal } from '../../hooks/useGlobal';
import { Msg } from '../../consts';
import { CardContainer } from '../../layout';
import {
  StatisticsModal,
  StatisticsCards,
  DeviceListTable,
  DeviceFilter,
} from './components';

const initialFilter: IDeviceListFilter = {
  q: '',
  clientStatus: '',
  isMessageSent: '',
  deviceType: '',
};

const filterEmptyParams = (params: any): Partial<IDeviceListFilter> => {
  const filteredDeviceFilter: Partial<IDeviceListFilter> = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value !== ''),
  );

  return filteredDeviceFilter;
};

export const DashboardPage: FC = () => {
  const { showAlert } = useGlobal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [statisticModal, setStatisticModal] = useState<boolean>(false);
  const [statisticsLoading, setStatisticsLoading] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<IStatistics>();
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState<boolean>(true);
  const [deviceFilter, setDeviceFilter] =
    useState<Partial<IDeviceListFilter>>(initialFilter);
  const [queueSchedule, setQueueSchedule] = useState<boolean>(false);
  const [queueScheduleLoading, setQueueScheduleLoading] =
    useState<boolean>(false);

  // update queue schedule
  const handleUpdateQueueSchedule = async () => {
    showAlert({
      text: `Mesaj gönderimi ${queueSchedule ? 'kapatılacak' : 'açılacak'}`,
      icon: 'warning',
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        setQueueScheduleLoading(true);

        try {
          await updateQueueSchedule({ enabled: !queueSchedule });

          setQueueSchedule(!queueSchedule);

          showAlert({
            text: Msg.Success,
            icon: 'success',
          });
        } catch (error) {
          console.error('handleUpdateQueueSchedule:', error);
        } finally {
          setQueueScheduleLoading(false);
        }
      }
    });
  };

  // fetch statistics
  const fetchStatistics = async () => {
    try {
      setStatisticsLoading(true);
      const response: any = await getQuickStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('fetchStatistics: ', error);
    } finally {
      setStatisticsLoading(false);
    }
  };

  // fetch queue schedule
  const fetchQueueSchedule = async () => {
    setQueueScheduleLoading(true);

    try {
      const response: any = await getQueueSchedule();
      setQueueSchedule(response.data?.queueSchedule || false);
    } catch (error) {
      console.error('fetchQueueSchedule:', error);
    } finally {
      setQueueScheduleLoading(false);
    }
  };

  const handleOpenStatisticsModal = () => {
    setStatisticModal(true);
  };

  // filter handler
  const handleFilter = () => {
    const filteredParams: Partial<IDeviceListFilter> =
      filterEmptyParams(deviceFilter);

    if (Object.keys(filteredParams).length > 0) {
      navigate({
        pathname: '/',
        search: createSearchParams(filteredParams).toString(),
      });
    }
  };

  // fetch devices
  const fetchDevices = async (params?: Partial<IDeviceListFilter>) => {
    try {
      setDevicesLoading(true);
      const response: any = await getDevices(params);
      setDevices(response.data);
    } catch (error) {
      console.error('fetchDevices:', error);
    } finally {
      setDevicesLoading(false);
    }
  };

  useEffect(() => {
    fetchQueueSchedule();
    fetchStatistics();
  }, []);

  useEffect(() => {
    const filteredParams: Partial<IDeviceListFilter> = filterEmptyParams(
      Object.fromEntries(searchParams),
    );

    // set device filter
    setDeviceFilter(filteredParams);

    // fetch devices with params
    fetchDevices(filteredParams);
  }, [searchParams]);

  return (
    <>
      <StatisticsCards
        statistics={statistics}
        onOpenStatisticsModal={handleOpenStatisticsModal}
      />

      <CardContainer title="Cihaz Listesi">
        <DeviceFilter
          deviceFilter={deviceFilter}
          setDeviceFilter={setDeviceFilter}
          handleFilter={handleFilter}
          onUpdateQueueSchedule={handleUpdateQueueSchedule}
          queueSchedule={queueSchedule}
          queueScheduleLoading={queueScheduleLoading}
        />

        <div className="flex flex-col mt-4">
          <div className="rounded-lg">
            <DeviceListTable devices={devices} loading={devicesLoading} />
          </div>
        </div>
      </CardContainer>

      {/* İstatistik Modalı */}
      <StatisticsModal
        isOpen={statisticModal}
        onClose={() => setStatisticModal(false)}
        statistics={statistics}
        isLoading={statisticsLoading}
        onRefresh={fetchStatistics}
      />
    </>
  );
};
